"use client";
/**
 * A chapter hero's right-hand scene — a silent, looping clip. Used by /podcast
 * and /blog; both heroes share the .chapter-scene slot and the .scene-film
 * panel, so they share the player too.
 *
 * ‼ Playback is GATED, not automatic, and that is the whole reason this is a
 * component rather than a <video autoplay> in the page.
 *
 * The site has a global motion switch: SiteMotion writes html[data-motion] and
 * defaults it to OFF when the OS asks for reduced motion. An `autoplay`
 * attribute obeys neither that switch nor the OS. So the element ships with
 * preload="none" and NO autoplay, and this decides instead — which means with
 * motion off, or the hero scrolled past, the 478 KB file is never fetched at
 * all and the poster (13.5 KB) is the whole cost.
 *
 * aria-hidden + tabIndex -1: the clip is decorative. The hero's meaning is in
 * the h1 and the copy beside it, so exposing a silent loop to a screen reader
 * would add noise, not information.
 */
import { useEffect, useRef } from "react";

export default function HeroFilm({
    src,
    poster,
    className = "",
    bare = false,
    minWidth,
}: {
    src: string;
    poster: string;
    /** Extra class on the scene wrapper, for contexts that size it differently
        (the contracting pages use a tall panel, not the 440px hero scene). */
    className?: string;
    /** Drop the framed-panel furniture (corner reticle) — for a full-bleed
        background where the clip is the surface, not an object on it. */
    bare?: boolean;
    /** Only ever load/play at or above this viewport width. The contracting
        stage shows this panel on a phone because the <h1> is overlaid on it,
        but a decorative clip is not worth 300-400 KB on cellular — below the
        breakpoint the poster stands in and the mp4 is never fetched. */
    minWidth?: number;
}) {
    const video = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const el = video.current;
        if (!el) return;

        let onScreen = false;
        const motionOn = () => document.documentElement.dataset.motion !== "off";
        const wideEnough = () =>
            minWidth == null ||
            (window.matchMedia?.(`(min-width: ${minWidth}px)`)?.matches ?? true);

        const sync = () => {
            if (onScreen && motionOn() && wideEnough()) {
                // preload="none" means this first play() is also the first fetch.
                // A refusal is fine and expected on some browsers/power modes —
                // the poster simply stays, which is a correct resting state.
                void el.play().catch(() => {});
            } else {
                el.pause();
            }
        };

        const io = new IntersectionObserver(
            (entries) => {
                onScreen = entries[0]?.isIntersecting ?? false;
                sync();
            },
            { threshold: 0.15 },
        );
        io.observe(el);

        // The toggle flips an attribute on <html>, so watch that rather than
        // re-rendering: SiteMotion owns the state and this only follows it.
        const mo = new MutationObserver(sync);
        mo.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-motion"],
        });

        // A rotation across the breakpoint has to re-evaluate, or a phone turned
        // landscape keeps the poster while a tablet shrunk below it keeps playing.
        const mq =
            minWidth != null ? window.matchMedia(`(min-width: ${minWidth}px)`) : null;
        mq?.addEventListener("change", sync);

        return () => {
            io.disconnect();
            mo.disconnect();
            mq?.removeEventListener("change", sync);
        };
    }, [minWidth]);

    return (
        <div className={`chapter-scene scene-broadcast ${className}`.trim()} data-tilt>
            {!bare && (
                <div className="scene-reticle" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                    <i />
                </div>
            )}
            <figure className="scene-film">
                <video
                    ref={video}
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster={poster}
                    aria-hidden="true"
                    tabIndex={-1}
                >
                    <source src={src} type="video/mp4" />
                </video>
            </figure>
        </div>
    );
}
